import { AdsTargetingProvider } from '@folklore/ads';
import { useUser } from '@folklore/auth';
import { ReactNode } from 'react';

import { useVisitorAudiences } from './VisitorContext';

export function getDocumentTargeting(item: FullDocument | Collection | null) {
    if (item === null) {
        return null;
    }
    const { type, metadata = null, identifiers, identifier } = item;
    const {
        brands = [],
        sponsors = [],
        categories = null,
        topics = null,
        formats = null,
        tags = null,
        adsSettings = null,
    } = metadata || {};
    const {
        exclusive = false,
        targeting: customTargeting = {},
        safeContent = true,
    } = adsSettings || {};
    const [brand = null] = brands || [];
    return {
        pageType: type,
        brand: brand !== null ? brand.handle || brand.slug : null,
        brands: (brands || []).map(({ slug = null, handle = null }) => handle || slug),
        categories: [...(categories || []), ...(topics || []), ...(formats || [])].map(
            ({ slug = null, handle = null }) => handle || slug,
        ),
        tags: (tags || []).map(({ slug = null, handle = null }) => handle || slug),
        safeContent: safeContent ? 'true' : 'false',
        exclusive: exclusive ? 'true' : 'false',
        identifiers,
        identifier,
        isSponsored: sponsors !== null && sponsors.length > 0 ? 'true' : 'false',
        ...customTargeting,
    };
}

function getTaxonomyTargeting(item: Taxonomy | null) {
    if (item === null) {
        return null;
    }

    const { type, brand = null, adsSettings = null, slug: taxonomySlug = null } = item;
    const {
        exclusive = false,
        targeting: customTargeting = {},
        safeContent = taxonomySlug === null || taxonomySlug.indexOf('sex') === -1,
    } = adsSettings || {};

    return {
        pageType: type,
        brand: brand !== null ? brand.handle || brand.slug : null,
        brands:
            brand !== null ? [brand].map(({ slug = null, handle = null }) => handle || slug) : null,
        categories:
            type !== 'tag' ? [item].map(({ slug = null, handle = null }) => handle || slug) : null,
        tags:
            type === 'tag' ? [item].map(({ slug = null, handle = null }) => handle || slug) : null,
        safeContent: safeContent ? 'true' : 'false',
        exclusive: exclusive ? 'true' : 'false',
        ...customTargeting,
    };
}

function getUserTargeting(item: User | null) {
    if (item === null) {
        return null;
    }
    const { subscriptions } = item || {};
    const activeSubscriptions = (subscriptions || [])
        .filter(({ subscribed = false }) => subscribed)
        .map(({ id }) => id);
    return activeSubscriptions.length > 0
        ? {
              subscriptions: activeSubscriptions,
          }
        : {};
}

interface DocumentAdsTargetingProviderProps {
    document?: FullDocument | null;
    disabled?: boolean;
    children?: ReactNode;
}

export function DocumentAdsTargetingProvider({
    document = null,
    disabled = false,
    children = null,
}: DocumentAdsTargetingProviderProps) {
    const targeting = {
        ...getDocumentTargeting(document),
        disabled,
    };
    return <AdsTargetingProvider targeting={targeting}>{children}</AdsTargetingProvider>;
}

interface CollectionAdsTargetingProviderProps {
    collection?: Collection | null;
    disabled?: boolean;
    children?: ReactNode;
}

export function CollectionAdsTargetingProvider({
    collection = null,
    disabled = false,
    children = null,
}: CollectionAdsTargetingProviderProps) {
    const targeting = {
        ...getDocumentTargeting(collection),
        disabled,
    };
    return <AdsTargetingProvider targeting={targeting}>{children}</AdsTargetingProvider>;
}

interface TaxonomyAdsTargetingProviderProps {
    taxonomy?: Taxonomy | null;
    disabled?: boolean;
    children?: ReactNode;
}

export function TaxonomyAdsTargetingProvider({
    taxonomy = null,
    disabled = false,
    children = null,
}: TaxonomyAdsTargetingProviderProps) {
    const targeting = {
        ...getTaxonomyTargeting(taxonomy),
        disabled,
    };
    return <AdsTargetingProvider targeting={targeting}>{children}</AdsTargetingProvider>;
}

interface UserAdsTargetingProviderProps {
    children?: ReactNode;
}

export function UserAdsTargetingProvider({ children = null }: UserAdsTargetingProviderProps) {
    const user = useUser();
    const audiences = useVisitorAudiences();
    const targeting = {
        ...getUserTargeting(user),
        audiences,
    };
    return <AdsTargetingProvider targeting={targeting}>{children}</AdsTargetingProvider>;
}
